import { useEffect, useState, type ReactNode } from 'react'
import { App as AntdApp, ConfigProvider, theme as antdTheme } from 'antd'
import { useTranslation } from 'react-i18next'
import { ANTD_LOCALES } from '@/app/i18n/antdLocales'
import { currentLanguage, isRtl } from '@/app/i18n'
import { DarkModeContext } from './colorScheme'
import { darkTheme, lightTheme } from './tokens'

// `index.html` dagi `theme-color` shu ranglarga teng — brauzer paneli sahifa bilan qo'shilib ketadi.
const PAGE_BACKGROUND = {
  light: antdTheme.getDesignToken(lightTheme).colorBgLayout,
  dark: antdTheme.getDesignToken(darkTheme).colorBgLayout,
}

/**
 * Tema — mehmonga alohida almashtirish tugmasi berilmaydi (bitta qadamlik
 * sahifada har ortiqcha boshqaruv elementi diqqatni bo'ladi). O'rniga
 * qurilmaning o'z sozlamasiga (`prefers-color-scheme`) ergashadi va u
 * o'zgarsa jonli kuzatiladi — kafeda tunda qorong'i rejimga o'tgan
 * qurilma bu yerda ham qorong'i ochilsin.
 */
function useSystemDarkMode(): boolean {
  // ⚠️ STANDART — QORONG'I (egasi qarori, 2026-08-08).
  //
  // Ilgari boshlang'ich qiymat qurilmaning `prefers-color-scheme`
  // sozlamasidan olinardi. Endi mehmon sahifasi ham boshqa ikki ilova
  // bilan bir xil ochiladi: yorug' qurilmada ham qorong'i.
  //
  // ⚠️ Qurilma sozlamasini KUZATISH SAQLANADI: foydalanuvchi tunda
  // yorug' rejimga o'tsa, sahifa unga ergashadi. Ya'ni "standart
  // qorong'i, lekin qurilma qaroriga bo'ysunadi" — qat'iy qotirish
  // yorug' rejimdagi odamni qorong'ida ushlab turardi va bunday
  // sahifada almashtirish tugmasi ham yo'q.
  const [isDark, setIsDark] = useState(true)

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: light)')
    // Qurilma ANIQ yorug' rejimni so'ragan bo'lsa — unga ergashamiz.
    setIsDark(!media.matches)
    const listener = (event: MediaQueryListEvent) => setIsDark(!event.matches)
    media.addEventListener('change', listener)
    return () => media.removeEventListener('change', listener)
  }, [])

  return isDark
}

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const isDark = useSystemDarkMode()

  useEffect(() => {
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light'
    document.body.style.backgroundColor = isDark ? PAGE_BACKGROUND.dark : PAGE_BACKGROUND.light
  }, [isDark])

  const antdLocale = ANTD_LOCALES[currentLanguage()]
  void i18n.resolvedLanguage // til o'zgarishiga bog'liqlikni aniq ko'rsatadi

  return (
    <DarkModeContext.Provider value={isDark}>
      <ConfigProvider
        theme={{ ...(isDark ? darkTheme : lightTheme), algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}
        locale={antdLocale}
        direction={isRtl() ? 'rtl' : 'ltr'}
      >
        {/* antd `App` — `message`/`modal` ni hook orqali ishlatish uchun.
            Statik chaqiruv tema tokenlarini OLMAYDI va qorong'i rejimda
            oq quti bo'lib chiqadi. */}
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    </DarkModeContext.Provider>
  )
}
